import React, { useRef, useEffect, useState, useCallback } from 'react';

import clsx from 'clsx';

import IconCalendar from '@/assets/icons/icon-calendar.svg?react';
import IconChevronDown from '@/assets/icons/icon-arrow-down.svg?react';

import {
  CustomRangeCalendar,
  type DateRange,
} from '@/components/analytics/CustomRangeCalendar/CustomRangeCalendar';

import { type TimeRange } from '@/components/analytics/types';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';

export type { TimeRange };

interface TimeRangeSelectorProps {
  value?: TimeRange;
  onChange?: (value: TimeRange) => void;
  customRange?: DateRange | null;
  onCustomRangeChange?: (range: DateRange) => void;
  className?: string;
}

const TIME_RANGE_OPTIONS: TimeRange[] = ['7 Days', '30 Days', '90 Days'];

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value = '7 Days',
  onChange,
  customRange: customRangeProp = null,
  onCustomRangeChange,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [isCustomMode, setIsCustomMode] = useState(!!customRangeProp);
  const [customRange, setCustomRange] = useState<DateRange | null>(customRangeProp);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    setCustomRange(customRangeProp);
    setIsCustomMode(!!customRangeProp);
  }, [customRangeProp]);

  const isTabSelected = !isCustomMode;

  // Measure tab indicator position
  useEffect(() => {
    if (!containerRef.current || isCustomMode) return;
    const selectedIndex = TIME_RANGE_OPTIONS.indexOf(value);
    const buttons = containerRef.current.querySelectorAll<HTMLButtonElement>('[data-tab-button]');
    const btn = buttons[selectedIndex];
    if (btn) {
      setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
    }
  }, [value, isCustomMode]);

  const handleTabClick = useCallback(
    (option: TimeRange) => {
      setIsCustomMode(false);
      setCalendarOpen(false);
      setCustomRange(null);
      onChange?.(option);
    },
    [onChange],
  );

  const handleCalendarIconClick = useCallback(() => {
    setIsCustomMode(true);
  }, []);

  const handleCalendarApply = useCallback(
    (range: DateRange) => {
      setCustomRange(range);
      setCalendarOpen(false);
      onCustomRangeChange?.(range);
    },
    [onCustomRangeChange],
  );

  const handleCalendarCancel = useCallback(() => {
    setCalendarOpen(false);
    if (!customRange) {
      setIsCustomMode(false);
    }
  }, [customRange]);

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {/* Tab bar */}
      <div
        ref={containerRef}
        className="relative flex h-8 w-full items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]"
      >
        {/* Animated sliding indicator */}
        {isTabSelected && (
          <div
            className="bg-neutral-0 absolute z-0 h-8 rounded-md border border-neutral-100 transition-all duration-200 ease-in-out"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
        {isCustomMode && (
          <div className="bg-neutral-0 absolute right-0 z-0 h-8 w-8 rounded-md border border-neutral-100 transition-all duration-200 ease-in-out" />
        )}

        {TIME_RANGE_OPTIONS.map((option) => {
          const isSelected = isTabSelected && value === option;
          return (
            <button
              type="button"
              key={option}
              data-tab-button
              onClick={() => handleTabClick(option)}
              className={clsx(
                'text-button-sm relative z-10 h-8 flex-1 cursor-pointer rounded-md px-2 py-1.5 text-center transition-colors duration-200',
                isSelected ? 'font-semibold text-indigo-500' : 'font-medium text-neutral-400',
              )}
            >
              {option}
            </button>
          );
        })}
        <button
          type="button"
          aria-label="Select custom date range"
          onClick={handleCalendarIconClick}
          className={clsx(
            'relative z-10 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors',
            isCustomMode ? 'text-indigo-500' : 'text-neutral-400 hover:text-neutral-600',
          )}
        >
          <IconCalendar className="h-4 w-4" />
        </button>
      </div>

      {/* Custom range: date display + dropdown calendar */}
      {isCustomMode && (
        <DropdownMenu open={calendarOpen} onOpenChange={setCalendarOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={clsx(
                'flex h-8 w-full cursor-pointer items-center justify-between rounded-md bg-neutral-50 px-2',
                calendarOpen && 'outline-1 -outline-offset-1 outline-indigo-500',
              )}
            >
              <span className="text-xs leading-4 font-medium text-neutral-900">
                {customRange
                  ? `${formatDate(customRange.start)} → ${formatDate(customRange.end)}`
                  : 'Select date range'}
              </span>
              <IconChevronDown
                className={clsx(
                  'h-4 w-4 text-neutral-500 transition-transform duration-200',
                  calendarOpen && 'rotate-180',
                )}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-(--radix-dropdown-menu-trigger-width) p-0"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <CustomRangeCalendar
              value={customRange ?? undefined}
              onApply={handleCalendarApply}
              onCancel={handleCalendarCancel}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
